import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import {
  MessageCircleQuestion,
  Clock,
  Shield,
  Phone,
  BarChart3,
  ChevronRight,
  Check,
  ArrowUp,
  TrendingUp,
} from "lucide-react";
import logoUrl from "@/assets/waiterix-logo.png";
import { Link } from "wouter";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

// AI Team member data
const teamMembers = [
  {
    name: "AI Waiter",
    description: "Explains the menu, answers questions, recommends items, takes orders, and helps increase check size with natural upsells.",
    image: "/assets/ai-waiter.jpeg",
    tags: ["QR + device", "Multi-language", "Upsells"],
  },
  {
    name: "AI Receptionist",
    description: "Answers calls 24/7, provides hours/location/menu info, captures reservations, and routes special requests, reducing missed calls.",
    image: "/assets/ai-receptionist.jpeg",
    tags: ["Calls + SMS", "Reservations", "Routing"],
  },
  {
    name: "AI Business Specialist",
    description: "Turns operational data into clear actions, top selling items, low performers, staffing signals, and shift insights you can use immediately.",
    image: "/assets/ai-business-specialist.jpeg",
    tags: ["Dashboards", "Insights", "Performance"],
  },
];

// Outcome cards data
const outcomeCards = [
  {
    icon: MessageCircleQuestion,
    title: "Fewer staff interruptions",
    description: "Waiterix explains the menu, answers guest questions, handles modifications, takes orders, and processes payments. That means fewer call-outs to servers, fewer distractions, and more time spent delivering great service and food.",
  },
  {
    icon: Clock,
    title: "Faster ordering and higher turnover",
    description: "Guests order when they're ready, no waiting to flag down a server. Orders flow directly to the kitchen as clean, accurate tickets, reducing back-and-forth and keeping service moving during peak hours.",
  },
  {
    icon: Shield,
    title: "Built-in upsells that feel natural",
    description: "Context-aware recommendations for drinks, sides, and upgrades increase average check size without pressure or awkward sales tactics. Guests feel guided, not sold to.",
  },
  {
    icon: Phone,
    title: "Never miss a call or reservation",
    description: "Waiterix answers calls 24/7, shares hours, location, and menu information, captures reservations, and routes special requests,so opportunities aren't lost when staff are busy on the floor.",
  },
  {
    icon: BarChart3,
    title: "Clear insights, better decisions",
    description: "Beyond service, Waiterix turns operational data into actionable insights highlighting top-selling items, low performers, staffing signals, and shift level trends you can act on immediately.",
  },
  {
    icon: TrendingUp,
    title: "Relief that scales with demand",
    description: "Whether it's a slow afternoon or a packed Friday night, Waiterix scales instantly. No schedules. No call-outs. No added payroll.",
  },
];

// How it works steps
const howItWorksSteps = [
  {
    num: 1,
    title: "Place AI devices or QR codes on tables",
    description: "Guests use a device or scan to open your menu instantly (mobile-optimized). No app required.",
  },
  {
    num: 2,
    title: "Guests order by tapping or talking",
    description: "Waiterix answers questions, makes recommendations, and collects accurate orders.",
  },
  {
    num: 3,
    title: "Orders flow to the kitchen in real time",
    description: "Clean tickets, live status updates, fewer mistakes and remakes.",
  },
  {
    num: 4,
    title: "Payments",
    description: "Customers can pay online or later at the register and track their orders in real time.",
  },
];

// Comparison table data
const comparisonData = [
  { feature: "AI waiter that answers menu questions and recommends items", waiterix: true, typical: false },
  { feature: "Voice + conversation + QR ordering in one system", waiterix: true, typical: false },
  { feature: "Smart upsells that increase average ticket size", waiterix: true, typical: false },
  { feature: "Real-time order management with live status", waiterix: true, typical: true },
  { feature: "Multi-language guest support", waiterix: true, typical: false },
];

// Testimonials data
const testimonials = [
  {
    quote: "During rush, guests stopped waving us down for menu questions. Orders came in cleaner, with fewer mistakes.",
    role: "Owner-operator",
    business: "Fast casual (2 locations)",
  },
  {
    quote: "Upsells felt natural. We saw more drinks and add-ons without our servers pushing anything.",
    role: "GM",
    business: "Quick service",
  },
  {
    quote: "It felt like adding an extra team member, without the scheduling headaches.",
    role: "Owner",
    business: "Phone-heavy operation",
  },
];

// FAQ data
const faqItems = [
  {
    question: "What is Waiterix?",
    answer: "Waiterix is an end-to-end AI solution for restaurants and hospitality businesses. It provides a team of AI workers, including an AI Waiter, AI Receptionist, and AI Business Specialist, to handle guest interactions, ordering, payments, and operational insights without disrupting your kitchen or existing systems.",
  },
  {
    question: "How is Waiterix different from QR menus or tablets?",
    answer: "Traditional QR menus are static. Waiterix is conversational. Guests can ask questions, get recommendations, customize orders, and pay naturally, as if speaking to a server. The result is faster service, fewer errors, and a better guest experience.",
  },
  {
    question: "Does Waiterix replace my staff?",
    answer: "No. Waiterix is designed to support staff, not replace them. It removes repetitive tasks like order-taking, answering menu questions, and handling calls, so your team can focus on hospitality and service quality.",
  },
  {
    question: "How does Waiterix improve operations?",
    answer: "Waiterix helps by: Reducing front-of-house interruptions, Speeding up ordering and table turnover, Increasing average check size with natural upsells, Eliminating missed calls and reservations, and Turning operational data into clear, actionable insights.",
  },
  {
    question: "Is Waiterix easy for guests to use?",
    answer: "Yes. Guests interact naturally, no app downloads required. The experience is intuitive for all ages and supports multiple languages, making it ideal for diverse and high-traffic locations.",
  },
  {
    question: "Does Waiterix work with my existing systems?",
    answer: "Yes. Waiterix can work alongside your current POS and workflows or operate as a standalone solution. You choose how deeply you want to integrate.",
  },
  {
    question: "How long does setup take?",
    answer: "Setup is designed to be minimal. Most locations can go live quickly with guided onboarding. For higher levels of adoption, our team supports setup and staff training to ensure a smooth launch.",
  },
  {
    question: "Is guest and restaurant data secure?",
    answer: "Absolutely. Restaurants retain full ownership of their data. Waiterix follows industry-standard security practices and does not sell or monetize customer or restaurant data. (See our Privacy Policy and Terms of Service for details.)",
  },
  {
    question: "What if Waiterix isn't a good fit?",
    answer: "Most customers love Waiterix, but if it's not right for your operation, you're protected by our 30-day risk-free guarantee. Simply cancel and receive a full refund.",
  },
];

export default function Landing() {
  const [showDemoDialog, setShowDemoDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'demo' | 'try'>('demo');
  const [demoForm, setDemoForm] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [hasSubmittedLead, setHasSubmittedLead] = useState<boolean>(() => {
    return localStorage.getItem("waiterixLeadCaptured") === "true";
  });
  const [showMandatoryModal, setShowMandatoryModal] = useState(false);

  useEffect(() => {
    if (!hasSubmittedLead) {
      setShowMandatoryModal(true);
    }
  }, [hasSubmittedLead]);

  const submitToGoHighLevel = async (data: { name: string; email: string; phone: string }) => {
    const url = "https://services.leadconnectorhq.com/hooks/S1qleEtAhbnf0PBYEoRu/webhook-trigger/6c67c2d1-3215-406a-b68a-974bcf68cb07";
    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      console.log("[GHL] Lead submitted");
    } catch (err) {
      console.error("[GHL] Submission error:", err);
    }
  };

  const submitToHubSpot = async (data: { name: string; email: string; phone: string }, source: string) => {
    const portalId = "244843893";
    const formId = "b808aa91-1754-4c46-b1dc-c68e7f0912bb";
    const url = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

    // Map internal fields to HubSpot default fields
    const fields = [
      { name: "firstname", value: data.name },
      { name: "email", value: data.email },
      { name: "phone", value: data.phone }
    ];

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields,
          context: {
            pageUri: window.location.href,
            pageName: document.title,
            hutk: document.cookie.match(/hubspotutk=([^;]+)/)?.[1] // Extract HubSpot cookie if present
          },
          legalConsentOptions: {
            consent: {
              consentToProcess: true,
              text: "I agree to allow Waiterix to store and process my personal data.",
              communications: [
                {
                  value: true,
                  subscriptionTypeId: 999,
                  text: "I agree to receive marketing communications from Waiterix."
                }
              ]
            }
          }
        })
      });
      console.log(`[HubSpot] Lead submitted from ${source}`);
    } catch (err) {
      console.error("[HubSpot] Submission error:", err);
    }
  };

  const handleDemoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Store lead data locally
    localStorage.setItem("waiterixDemoLead", JSON.stringify({ ...demoForm, ts: Date.now(), source: dialogMode }));

    // Send to HubSpot
    submitToHubSpot(demoForm, `landing_${dialogMode}`);

    // Close dialog
    setShowDemoDialog(false);
    // If demo mode, open Calendly
    if (dialogMode === 'demo') {
      window.open('https://calendly.com/kabir-waiterix/30min?back=1', '_blank');
    }
    // Reset form
    setDemoForm({ name: "", email: "", phone: "" });
  };

  const openDemoDialog = () => {
    setDialogMode('demo');
    setShowDemoDialog(true);
  };

  const tryAIWaiter = () => {
    // Open the menu link with showLeadForm flag
    window.open('https://www.waiterix.com/menu/83b5e11f-46cf-4572-a383-d5806f73966c?table=2&showLeadForm=true', '_blank');
  };

  const handleMandatorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Submit to GHL
    await submitToGoHighLevel(demoForm);

    // Also submit to HubSpot for consistency if desired
    submitToHubSpot(demoForm, "mandatory_landing");

    // Store lead data locally
    localStorage.setItem("waiterixLeadCaptured", "true");
    localStorage.setItem("waiterixDemoLead", JSON.stringify({ ...demoForm, ts: Date.now(), source: "mandatory" }));

    setHasSubmittedLead(true);
    setShowMandatoryModal(false);
    // Reset form for demo dialog if they use it later
    setDemoForm({ name: "", email: "", phone: "" });
  };

  return (
    <>
      <div className={cn(
        "min-h-screen bg-background transition-all duration-500",
        showMandatoryModal && "blur-xl grayscale-[0.5] pointer-events-none select-none scale-[0.98] origin-center"
      )}>
        {/* Navigation Header */}
        <header className="border-b glass backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-3">
              <img src={logoUrl} alt="Waiterix" className="h-10 hover-lift smooth-transition" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
              <a href="#outcomes" className="hover:text-foreground transition-colors">Outcomes</a>
              <a href="#team" className="hover:text-foreground transition-colors">The Future team</a>
              <a href="#how" className="hover:text-foreground transition-colors">How it works</a>

              <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
            </nav>

            <div className="flex items-center gap-2">
              <LanguageSelector />
              <ThemeToggle />
              <Button onClick={openDemoDialog} className="btn-glow shadow-glow hover-lift">
                <span>Book a demo</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-orange-400/10"></div>
          <div className="absolute top-20 left-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl float"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-orange-400/10 rounded-full blur-3xl float-delayed"></div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Column - Text */}
              <div className="space-y-6">

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  Is your restaurant short-staffed?
                  <br />
                  <span className="text-primary">Waiterix picks up the slack.</span>
                </h1>

                <p className="text-lg text-muted-foreground max-w-2xl">
                  Waiterix is an end-to-end AI solution for restaurants and hospitality businesses. We provide a team of AI workers that handles guest interactions, ordering, payments, and data-driven decision support, seamlessly and at scale. Waiterix reduces front-of-house workload, speeds up service, improves customer satisfaction, and increases revenue, without changing how your kitchen or existing systems operate
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4">
                  <Button onClick={tryAIWaiter} className="btn-glow shadow-glow">Try the AI waiter (2 mins quick trial)</Button>
                  <Button size="lg" variant="outline" onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })}>
                    How it works
                  </Button>
                </div>

                {/* Trust Markers */}
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <Check className="h-3 w-3 text-primary" />
                    </span>
                    Minimal setup
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <Check className="h-3 w-3 text-primary" />
                    </span>
                    Works with your current system
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <Check className="h-3 w-3 text-primary" />
                    </span>
                    Cancel anytime
                  </span>
                </div>
              </div>

              {/* Right Column - The Squad Image */}
              <div className="flex flex-col gap-4 items-center md:items-start">

                <Card className="glass-card shadow-2xl overflow-hidden border-0 max-w-md w-full">
                  <img
                    src="/assets/squad.png"

                    className="w-full h-auto object-cover rounded-xl"
                    onError={(e) => {
                      // Log error and show placeholder
                      console.error("Failed to load squad image");
                      (e.target as HTMLImageElement).src = "https://placehold.co/600x600/f3f4f6/9ca3af?text=The+Squad";
                    }}
                  />
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Outcomes Section */}
        <section id="outcomes" className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">We sell relief, not software.</h2>
              <p className="text-muted-foreground text-lg max-w-2xl">
                Our clients don't buy AI. They buy smoother shifts, fewer mistakes, and more revenue, without hiring more people or overhauling their operations.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {outcomeCards.map((card, index) => (
                <Card key={index} className="p-6 glass-card shadow-card-hover group relative overflow-hidden">
                  <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-primary/10 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                      <card.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{card.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Meet Your Team Section */}
        <section id="team" className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Meet your future team</h2>
                <p className="text-muted-foreground text-lg max-w-2xl">
                  Three AI roles that cover guest ordering, inbound calls, and business insights, so your staff can focus on hospitality.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={openDemoDialog}>Get the demo</Button>
                <Button onClick={tryAIWaiter} className="btn-glow shadow-glow">Try the AI waiter (2 mins quick trial)</Button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {teamMembers.map((member, index) => (
                <Card key={index} className="p-6 bg-card shadow-lg border overflow-hidden">
                  <div className="aspect-square rounded-xl overflow-hidden border mb-4">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        // Fallback to a placeholder on error
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{member.name}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">{member.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {member.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-3 py-1 text-xs font-semibold rounded-full bg-primary/10 border border-primary/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how" className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">How Waiterix works</h2>
              <p className="text-muted-foreground text-lg max-w-2xl">
                Use it alongside staff today, or let it handle peak-hour ordering automatically. No app downloads for guests.
              </p>
            </div>

            <div className="space-y-4">
              {/* Steps */}
              {howItWorksSteps.map((step) => (
                <div key={step.num} className="flex gap-4 p-4 rounded-xl bg-card/50 border">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-orange-400/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-primary">{step.num}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">{step.title}</h4>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}

              {/* Comparison Table - Commented out for now
            <Card id="compare" className="mt-6 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b bg-muted/50">
                <h4 className="font-semibold">Our Capability</h4>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold">
                  <span className="w-2 h-2 rounded-full bg-primary"></span>
                  Omnichannel ordering
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-4 font-semibold w-[40%]">Capability</th>
                      <th className="text-left p-4 font-semibold">Waiterix</th>
                      <th className="text-left p-4 font-semibold">Typical QR ordering</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row, index) => (
                      <tr key={index} className="border-b last:border-0">
                        <td className="p-4 text-muted-foreground">{row.feature}</td>
                        <td className="p-4">
                          <span className={row.waiterix ? "text-primary font-bold" : "text-muted-foreground"}>
                            {row.waiterix ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={row.typical ? "text-primary font-bold" : "text-muted-foreground"}>
                            {row.typical ? "Sometimes" : "Usually no"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            */}
            </div>
          </div>
        </section>

        {/* Testimonials Section - Commented out for now
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Operator-relevant proof</h2>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Early proof can be anonymized. The goal is to show measurable shift outcomes—not vanity metrics.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="p-6 glass-card shadow-card-hover">
                <p className="text-sm leading-relaxed mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold">{testimonial.role}</span>
                  <span className="text-muted-foreground">{testimonial.business}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
      */}

        {/* FAQ Section */}
        <section id="faq" className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-muted-foreground text-lg max-w-2xl">
                Everything you need to know about Waiterix and how it works for your restaurant.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {faqItems.map((item, index) => (
                <details
                  key={index}
                  className="p-4 rounded-xl border bg-card/50 group"
                  open={index === 0}
                >
                  <summary className="font-semibold cursor-pointer list-none flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                    {item.question}
                  </summary>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed pl-6">
                    {item.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <Card className="p-8 glass-card relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-gradient-to-br from-primary/20 to-orange-400/10 rounded-full blur-3xl"></div>
              <div className="flex flex-wrap items-center justify-between gap-6 relative z-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">See how Waiterix would run your restaurant</h2>
                  <p className="text-muted-foreground">2 minutes. No commitment. Built for real shifts and real margins.</p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                    <ArrowUp className="h-4 w-4 mr-2" />
                    Back to top
                  </Button>
                  <Button onClick={openDemoDialog} className="btn-glow shadow-glow hover-lift">
                    Book a Demo
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>


        {/* Footer */}
        <footer className="border-t py-12 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center">

              {/* Powered By */}
              <div className="mb-8">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                  Powered by
                </p>

                <div className="flex justify-center items-center gap-8 opacity-80">
                  <img
                    src="/assets/openai.png"
                    alt="Powered by OpenAI"
                    className="h-6 grayscale hover:grayscale-0 transition-all duration-300"
                  />
                  <img
                    src="/assets/stripe.png"
                    alt="Powered by Stripe"
                    className="h-6 grayscale hover:grayscale-0 transition-all duration-300"
                  />
                  <img
                    src="/assets/twilio.png"
                    alt="Powered by Twilio"
                    className="h-6 grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
              </div>

              {/* Footer Links */}
              <div className="flex justify-center gap-6 mb-4 text-sm">
                <Link
                  href="/terms"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="link-terms"
                >
                  Terms & Conditions
                </Link>
                <span className="text-muted-foreground">•</span>
                <Link
                  href="/privacy"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="link-privacy"
                >
                  Privacy Policy
                </Link>
              </div>

              <p className="text-sm text-muted-foreground">
                © 2025 Waiterix. All rights reserved.
              </p>

              {/* Disclaimer */}
              <p className="text-xs text-muted-foreground/70 mt-6 max-w-6xl mx-auto leading-relaxed">
                All information, services, products, and content on this website are provided for general informational purposes only and should not be considered professional advice. No rights or guarantees can be derived from the content on this site, and use of the website is governed by our Terms of Use. Reported results are based on either extrapolation or feedback from individual clients and may vary from one restaurant to another.
              </p>

              <p className="text-sm font-medium text-muted-foreground mt-2">
                A product of Harmonia Enterprises LLC
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Demo Booking Dialog */}
      <Dialog open={showDemoDialog} onOpenChange={() => { }}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'demo' ? 'Book a Demo' : 'Enjoying the AI Waiter?'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'demo'
                ? "Fill in your details and we'll redirect you to schedule your demo."
                : "Leave your details and we'll reach out to help you get started with Waiterix."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleDemoSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="demo-name">Name</Label>
              <Input
                id="demo-name"
                placeholder="Your name"
                value={demoForm.name}
                onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="demo-email">Email</Label>
              <Input
                id="demo-email"
                type="email"
                placeholder="you@example.com"
                value={demoForm.email}
                onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="demo-phone">Phone Number</Label>
              <Input
                id="demo-phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={demoForm.phone}
                onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                required
              />
            </div>

            <Button type="submit" className="w-full btn-glow shadow-glow">
              {dialogMode === 'demo' ? 'Book a Demo' : 'Submit'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={showMandatoryModal} onOpenChange={() => { }}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden shadow-2xl border-primary/20"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              Welcome to Waiterix
            </DialogTitle>
            <DialogDescription className="text-center text-base">
              Please enter your details to explore how Waiterix can transform your restaurant.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleMandatorySubmit} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="m-name">Full Name</Label>
              <Input
                id="m-name"
                placeholder="John Doe"
                value={demoForm.name}
                onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                required
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="m-phone">Phone Number</Label>
              <Input
                id="m-phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={demoForm.phone}
                onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                required
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="m-email">Email Address</Label>
              <Input
                id="m-email"
                type="email"
                placeholder="john@restaurant.com"
                value={demoForm.email}
                onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                required
                className="h-12"
              />
            </div>

            <Button type="submit" className="w-full h-12 text-base font-semibold btn-glow shadow-glow mt-2">
              Access Landing Page
            </Button>

            <p className="text-[10px] text-center text-muted-foreground px-4">
              By submitting, you agree to our Terms of Service and Privacy Policy. We'll use your info to provide personalized insights.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
